import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    ChevronDown,
    ChevronRight,
    Heart,
    PackageCheck,
    Star,
    Truck,
    Zap,
} from "lucide-react";
import { useWishlist } from "../../../../../context/WishlistContext";
import MenProductOptionsModal from "./MenProductOptionsModal/MenProductOptionsModal";
import {
    FashionProductItem,
    mapBrowseProductToFashion,
    formatPrice,
} from "../fashionTypes";
import "./MenFashionSection.css";

const PRODUCT_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || ""}${process.env.REACT_APP_ECOMMERCE_PERFIX || ""}`;

const menFashionCache = new Map<string, FashionProductItem[]>();

interface MenFashionSectionProps {
    superCategory?: string | null;
    categoryName?: string | null;
    category?: any;
    subCategory?: string | null;
    title?: string;
    subtitle?: string;
    eyebrow?: string;
    limit?: number;
    promoBanner?: React.ReactNode;
    bannerPosition?: "top" | "bottom";
}

const MenFashionSection: React.FC<MenFashionSectionProps> = ({
    superCategory,
    categoryName,
    category: _category,
    subCategory: _subCategory,
    title,
    subtitle,
    eyebrow,
    limit = 6,
    promoBanner,
    bannerPosition = "top",
}) => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const { isItemWishlisted, toggleWishlist: toggleWishlistContext } = useWishlist();

    const targetSuperCategory = (superCategory || categoryName || "").trim();
    const activeCategory = targetSuperCategory || "Featured";

    const [products, setProducts] = useState<FashionProductItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [selectedColors, setSelectedColors] = useState<Record<string | number, number>>({});
    const [selectedProduct, setSelectedProduct] = useState<FashionProductItem | null>(null);

    // Lazy load when scrolled near viewport
    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: "240px 0px" }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Fetch dynamic products from browse API ONLY by superCategory
    useEffect(() => {
        if (!isVisible || !targetSuperCategory) return;

        const cacheKey = `superCat-${targetSuperCategory}-${limit}`;
        const cached = menFashionCache.get(cacheKey);
        if (cached) {
            setProducts(cached);
            setHasLoaded(true);
            return;
        }

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const params = {
                    superCategory: targetSuperCategory,
                    limit,
                    sort: "relevance",
                };

                const res = await axios.get(
                    `${PRODUCT_BASE_URL}/e-commerce/catalog/categories/browse`,
                    { params }
                );

                const rawItems = res.data?.data?.products || [];

                if (!cancelled) {
                    if (rawItems.length > 0) {
                        const isWomen = targetSuperCategory.toLowerCase().includes("women");
                        const mapped = rawItems.map((item: any, idx: number) =>
                            mapBrowseProductToFashion(item, isWomen ? "women" : "men", idx)
                        );
                        menFashionCache.set(cacheKey, mapped);
                        setProducts(mapped);
                    } else {
                        menFashionCache.set(cacheKey, []);
                        setProducts([]);
                    }
                    setHasLoaded(true);
                }
            } catch {
                if (!cancelled) {
                    setProducts([]);
                    setHasLoaded(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [isVisible, targetSuperCategory, limit]);

    const selectColor = (productId: string | number, colorIndex: number) => {
        setSelectedColors((prev) => ({
            ...prev,
            [productId]: colorIndex,
        }));
    };

    const handleWishlistToggle = (e: React.MouseEvent, product: FashionProductItem) => {
        e.stopPropagation();
        if (toggleWishlistContext) {
            toggleWishlistContext(String(product.id));
        }
    };

    const handleViewAll = () => {
        if (targetSuperCategory) {
            navigate(`/Categories/${encodeURIComponent(targetSuperCategory)}`);
        }
    };

    const handleProductClick = (product: FashionProductItem) => {
        const targetId = product._id || product.id || product.ssin;
        const superCat = product.categoryHierarchy?.superCategory || targetSuperCategory;
        const breadcrumb = [
            "Home",
            superCat,
            product.categoryHierarchy?.category,
            product.categoryHierarchy?.subCategory,
            product.title,
        ].filter(Boolean) as string[];

        navigate(`/DetailPage/${targetId}`, {
            state: {
                product: {
                    ...product,
                    category: superCat,
                    breadcrumb,
                },
            },
        });
    };

    if (!isVisible) {
        return <div ref={sectionRef} style={{ minHeight: 1 }} aria-hidden="true" />;
    }

    // If data was fetched and nothing was found, return null in place of the whole component
    if (hasLoaded && products.length === 0) {
        return null;
    }

    if (loading && products.length === 0) {
        return (
            <div ref={sectionRef} className="container-fluid px-3 py-3">
                <div className="d-flex gap-3 overflow-hidden">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div
                            key={i}
                            className="flex-shrink-0"
                            style={{
                                width: 260,
                                height: 420,
                                borderRadius: 16,
                                background: "#f0f2f5",
                            }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    if (products.length === 0) {
        return null;
    }

    const isMen = activeCategory.toLowerCase() === "men";
    const isWomen = activeCategory.toLowerCase().includes("women");

    const headerEyebrow =
        eyebrow ||
        (isMen
            ? "CURATED FOR HIM"
            : isWomen
            ? "CURATED FOR HER"
            : "FEATURED COLLECTION");

    const headerTitle =
        title ||
        (isMen
            ? "Men Fashion & Accessories"
            : isWomen
            ? "Women Fashion & Accessories"
            : activeCategory.toLowerCase().includes("collection")
            ? activeCategory
            : `${activeCategory}`);

    const headerSubtitle =
        subtitle ||
        (isMen
            ? "Premium fits, trending styles and everyday essentials selected for you."
            : isWomen
            ? "Trending styles, chic outfits and everyday essentials curated for you."
            : `Explore top deals and popular items in ${activeCategory}.`);

    return (
        <div ref={sectionRef}>
            {bannerPosition === "top" && promoBanner}
            <section className="men-fashion">
                <div className="men-fashion-container">
                    <div className="men-fashion-header">
                        <div>
                            <span className="men-fashion-eyebrow">
                                {headerEyebrow}
                            </span>
                            <h2>{headerTitle}</h2>
                            <p>{headerSubtitle}</p>
                        </div>

                        <button
                            type="button"
                            className="men-fashion-view-all"
                            onClick={handleViewAll}
                        >
                            View all
                            <ChevronRight
                                size={16}
                                strokeWidth={2}
                            />
                        </button>
                    </div>

                    <div className="men-fashion-grid">
                        {products.map((product) => {
                            const discount = Math.round(
                                ((product.mrp - product.price) /
                                    product.mrp) *
                                    100
                            );

                            const isWishlisted = isItemWishlisted(String(product.id));
                            const selectedColorIndex =
                                selectedColors[product.id] ?? 0;
                            const activeColor =
                                product.colors[selectedColorIndex] ||
                                product.colors[0];
                            const displayedImage =
                                activeColor?.image || product.image;

                            return (
                                <article
                                    className="men-fashion-product-card men-product-card"
                                    key={product.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={(event) => {
                                        const target = event.target as HTMLElement;
                                        if (target.closest("button")) return;
                                        handleProductClick(product);
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            handleProductClick(product);
                                        }
                                    }}
                                >
                                    <div className="men-fashion-product-image-wrapper men-product-image-wrapper">
                                        <span className="men-fashion-product-badge men-product-badge">
                                            {product.badge}
                                        </span>

                                        <button
                                            type="button"
                                            className={`men-fashion-product-wishlist men-product-wishlist ${isWishlisted
                                                ? "active"
                                                : ""
                                                }`}
                                            onClick={(e) =>
                                                handleWishlistToggle(e, product)
                                            }
                                            aria-label={
                                                isWishlisted
                                                    ? "Remove from wishlist"
                                                    : "Add to wishlist"
                                            }
                                        >
                                            <Heart
                                                size={16}
                                                strokeWidth={2}
                                                fill={
                                                    isWishlisted
                                                        ? "currentColor"
                                                        : "none"
                                                }
                                            />
                                        </button>

                                        <img
                                            src={displayedImage}
                                            alt={product.title}
                                            className="men-fashion-product-image men-product-image"
                                            onError={(e) => {
                                                if (product.image && e.currentTarget.src !== product.image) {
                                                    e.currentTarget.src = product.image;
                                                } else {
                                                    e.currentTarget.src =
                                                        "/assets/images/products/men-fas/men-1.png";
                                                }
                                            }}
                                        />
                                    </div>

                                    <div className="men-fashion-product-info men-product-info">
                                        {/* Color swatches row: container always rendered to guarantee vertical alignment */}
                                        <div className="men-fashion-product-colors men-product-colors">
                                            {product.colors && product.colors.length > 0 ? (
                                                <>
                                                    <div className="men-fashion-product-color-list men-product-color-list">
                                                        {product.colors.slice(0, 4).map(
                                                            (color, index) => (
                                                                <button
                                                                    type="button"
                                                                    key={color.name || index}
                                                                    className={`men-fashion-product-color men-product-color ${selectedColorIndex ===
                                                                        index
                                                                        ? "active"
                                                                        : ""
                                                                        }`}
                                                                    title={color.name}
                                                                    aria-label={`Select ${color.name}`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        selectColor(
                                                                            product.id,
                                                                            index
                                                                        );
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            backgroundColor:
                                                                                color.value,
                                                                        }}
                                                                    />
                                                                </button>
                                                            )
                                                        )}
                                                    </div>

                                                    {product.extraColors > 0 && (
                                                        <button
                                                            type="button"
                                                            className="men-fashion-product-more-colors men-product-more-colors"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedProduct(product);
                                                            }}
                                                        >
                                                            +{product.extraColors} more
                                                        </button>
                                                    )}
                                                </>
                                            ) : null}
                                        </div>

                                        <h3>{product.title}</h3>

                                        <p className="men-fashion-product-description men-product-description">
                                            {product.description.length > 80
                                                ? product.description.slice(0, 80) + "..."
                                                : product.description}
                                        </p>


                                        <div className="men-fashion-product-rating men-product-rating">
                                            <strong>
                                                {product.rating.toFixed(1)}
                                            </strong>

                                            <span className="men-fashion-product-stars men-product-stars">
                                                {[1, 2, 3, 4, 5].map(
                                                    (star) => (
                                                        <Star
                                                            key={star}
                                                            size={11}
                                                            strokeWidth={1.8}
                                                            fill={
                                                                star <=
                                                                    Math.round(
                                                                        product.rating
                                                                    )
                                                                    ? "currentColor"
                                                                    : "none"
                                                                }
                                                        />
                                                    )
                                                )}
                                            </span>

                                            <span className="men-fashion-product-review-count men-product-review-count">
                                                ({product.reviews})
                                            </span>
                                        </div>
                                        {product.deal ? (
                                        <div
                                            className={`men-fashion-product-deal men-product-deal ${
                                                product.deal
                                                    ? product.dealType || "limited"
                                                    : "empty-deal"
                                            }`}
                                        >
                                          
                                                <>
                                                    <span className="men-fashion-product-deal-dot men-product-deal-dot" />
                                                    <strong>
                                                        {product.deal}
                                                    </strong>
                                                </>
                                            
                                        </div>
) : null}
                                        <div className="men-fashion-product-pricing men-product-pricing">
                                            <div className="men-fashion-product-price-main men-product-price-main">
                                                <strong>
                                                    <sup>₹</sup> {formatPrice(
                                                        product.price
                                                    )}
                                                </strong>

                                                {discount > 0 && (
                                                    <span className="men-fashion-product-discount men-product-discount">
                                                        {discount}% OFF
                                                    </span>
                                                )}
                                            </div>

                                            <div
                                                className={`men-fashion-product-price-secondary men-product-price-secondary ${
                                                    product.mrp > product.price
                                                        ? ""
                                                        : "price-secondary-hidden"
                                                }`}
                                            >
                                                {product.mrp > product.price ? (
                                                    <>
                                                        <span>MRP</span>
                                                        <del>
                                                            ₹
                                                            {formatPrice(
                                                                product.mrp
                                                            )}
                                                        </del>
                                                        <span className="men-fashion-product-saving men-product-saving">
                                                            Save ₹
                                                            {formatPrice(
                                                                product.mrp -
                                                                product.price
                                                            )}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span>&nbsp;</span>
                                                )}
                                            </div>
                                        </div>

                                        {product.delivery && (
                                            <div
                                                className={`men-fashion-product-delivery men-product-delivery ${
                                                    product.deliveryType || "delivery"
                                                }`}
                                            >
                                                {(product.deliveryType === "delivery" ||
                                                    !product.deliveryType) && (
                                                    <Truck
                                                        size={15}
                                                        strokeWidth={1.9}
                                                    />
                                                )}

                                                {product.deliveryType === "fast" && (
                                                    <Zap
                                                        size={15}
                                                        strokeWidth={2}
                                                    />
                                                )}

                                                {product.deliveryType === "stock" && (
                                                    <PackageCheck
                                                        size={15}
                                                        strokeWidth={1.9}
                                                    />
                                                )}

                                                <span>{product.delivery}</span>
                                            </div>
                                        )}

                                        <button
                                            type="button"
                                            className="men-fashion-product-options men-product-options"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedProduct(product);
                                            }}
                                        >
                                            Select Options
                                            <ChevronDown
                                                size={15}
                                                strokeWidth={2}
                                            />
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>
            {bannerPosition === "bottom" && promoBanner}

            <MenProductOptionsModal
                product={selectedProduct}
                onClose={() => setSelectedProduct(null)}
            />
        </div>
    );
};

export default MenFashionSection;